<template>
  <div class="biomasa-form card shadow-sm p-4">
    <h3 class="mb-4 d-flex align-items-center">
      <font-awesome-icon :icon="['fas', isEdit ? 'edit' : 'seedling']" class="me-2" />
      {{ isEdit ? 'Editar' : 'Nueva' }} Biomasa
    </h3>

    <!-- PASO 1: Selección de Plantas -->
    <div v-if="step === 1">
      <div class="card mb-4 border-success">
        <div class="card-header text-white" style="background-color: #28a745;">
          <font-awesome-icon icon="fas fa-seedling" class="me-2" />
          Vincular Plantas a la Biomasa
        </div>
        <div class="card-body">
          <seleccionarplantas-lote :lote-id="lote_id" :proyecto_id="proyecto_id"
            :proyecto_evento_id="proyecto_evento_id" :club_id="club_id"
            :estados-filtrados="['Viva', 'Lista para cosechar', 'Dañada', 'Enferma']"
            @plantas-seleccionadas="storePlantasSeleccionadas" />
        </div>
      </div>

      <!-- Botón avanzar -->
      <div class="d-flex justify-content-end mt-3">
        <button class="btn btn-primary" @click="irAStep2">Siguiente paso</button>
      </div>
    </div>

    <!-- PASO 2: Datos de biomasa -->
    <form v-if="step === 2" @submit.prevent="handleSubmit">
      <div class="mb-3">
        <button type="button" class="btn btn-outline-secondary" @click="step = 1">
          ← Volver
        </button>
      </div>

      <div class="form-check form-switch mb-3">
        <input id="cambiar-estado" class="form-check-input" type="checkbox" v-model="cambiarEstadoPlantas" />
        <label for="cambiar-estado" class="form-check-label text-danger fw-semibold">
          Marcar plantas seleccionadas como "Cosechada"
        </label>
      </div>
      <div v-if="cambiarEstadoPlantas" class="alert alert-warning d-flex align-items-center">
        <font-awesome-icon icon="fas fa-exclamation-triangle" class="me-2" />
        <div>Una vez marcadas como “Cosechada”, las plantas quedarán inhabilitadas y no se podrán volver a usar. 
          Si desea seguir cosechando Biomasa de estas Plantas, no las marque como "Cosechada"</div>
      </div>

      <!-- Formulario datos -->
      <div class="row g-3">
        <div class="row g-3">
          <!-- Fecha de cosecha -->
          <div class="col-md-6">
            <label for="fecha_cosecha" class="form-label">Fecha de cosecha</label>
            <input type="date" id="fecha_cosecha" v-model="form.fecha_cosecha" class="form-control" required />
          </div>

          <!-- Tipo de biomasa -->
          <div class="col-md-6">
            <label for="tipo_biomasa" class="form-label">Tipo de biomasa</label>
            <select id="tipo_biomasa" v-model="form.tipo_biomasa" class="form-select" required>
              <option disabled value="">Seleccione tipo</option>
              <option v-for="tipo in tiposBiomasa" :key="tipo.id" :value="tipo.id">
                {{ tipo.descripcion }}
              </option>
            </select>
          </div>

          <!-- Peso -->
          <div class="col-md-6">
            <label for="peso" class="form-label">Peso</label>
            <input type="number" step="0.01" min="0" id="peso" v-model="form.peso" class="form-control" required />
          </div>

          <!-- Unidad de medida -->
          <div class="col-md-6">
            <label for="unidad_medida" class="form-label">Unidad de medida</label>
            <select id="unidad_medida" v-model="form.unidad_medida" class="form-select" required>
              <option disabled value="">Seleccione unidad</option>
              <option v-for="unidad in unidadesMedida" :key="unidad" :value="unidad">
                {{ unidad }}
              </option>
            </select>
          </div>

          <!-- Observaciones -->
          <div class="col-12">
            <label for="observaciones" class="form-label">Observaciones (opcional)</label>
            <textarea id="observaciones" v-model="form.observaciones" rows="3" class="form-control"></textarea>
          </div>
        </div>
      </div>

      <div class="mt-4 d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-outline-secondary" @click="$emit('cancel')">Cancelar</button>
        <button type="submit" class="btn btn-success" :disabled="loading">
          <font-awesome-icon :icon="['fas', isEdit ? 'save' : 'check']" class="me-1" />
          {{ isEdit ? 'Guardar Cambios' : 'Crear Biomasa' }}
        </button>
      </div>
    </form>
  </div>
</template>





<script>
import axios from '@/axios'
import seleccionarplantasLote from '../Lote/seleccionarplantasLote.vue'

export default {
  name: 'BiomasaForm',
  components: { seleccionarplantasLote },
  props: {
    proyecto_id: { type: Number, required: true },
    proyecto_evento_id: { type: Number, required: true },
    biomasa: { type: Object, default: null }
  },
  data() {
    return {
      lote_id: null,
      club_id: this.$store.getters.getClubActual.id,
      unidadesMedida: [],
      tiposBiomasa: [],
      step: 1, // 1: Selección de Plantas, 2: Datos de Biomasa
      form: {
        fecha_cosecha: '',
        tipo_biomasa: '',
        peso: null,
        unidad_medida: '',
        observaciones: ''
      },
      cambiarEstadoPlantas: false,
      plantasSeleccionadas: [],
      loading: false
    }
  },
  computed: {
    isEdit() {
      return !!this.biomasa
    },
    today() {
      const d = new Date()
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      return `${d.getFullYear()}-${mm}-${dd}`
    }
  },

  async created() {
    if (!this.proyecto_evento_id) {
      alert('Hubo un error: No se ha seleccionado un evento de proyecto.')
      return
    }

    if (!this.isEdit) {
      this.form.fecha_cosecha = this.today
    }
    try {
      const [uRes, tRes] = await Promise.all([
        axios.get('/api/constantes/unidades-medida'),
        axios.get('/api/constantes/tipos_biomasa')
      ])
      this.unidadesMedida = uRes.data
      this.tiposBiomasa = tRes.data

      //filtrar unidades de medida quitar distancias y temperaturas
      this.unidadesMedida = this.unidadesMedida.filter(u => {
        return !['kilometros', 'metros', 'centimetros', 'grados celsius', 'grados fahrenheit'].includes(u)
      })
    } catch (e) {
      console.error('Error cargando constantes:', e)
    }
    if (this.isEdit) {
      const { fecha_cosecha, tipo_biomasa, peso, unidad_medida, observaciones } = this.biomasa
      this.form = {
        fecha_cosecha: fecha_cosecha.slice(0, 10),
        tipo_biomasa,
        peso,
        unidad_medida,
        observaciones: observaciones || ''
      }
    }
  },
  methods: {
    storePlantasSeleccionadas(list) {
      this.plantasSeleccionadas = list
    },
    irAStep2() {
      if (this.plantasSeleccionadas.length === 0) {
        alert('Debe seleccionar al menos una planta para continuar.');
        return;
      }
      this.lote_id = this.plantasSeleccionadas[0].lote_id;
      this.step = 2;
    },

    async handleSubmit() {


      if (this.plantasSeleccionadas.length === 0) {
        alert('Debe seleccionar al menos una planta.')
        return
      }


      //obtener lote_id de la planta seleccionada
      if (this.plantasSeleccionadas.length > 0) {
        this.lote_id = this.plantasSeleccionadas[0].lote_id
      }


      if (this.cambiarEstadoPlantas) {
        const msg =
          'Las plantas seleccionadas pasarán a estado "Cosechada" ' +
          'y no podrán usarse de nuevo. ¿Desea continuar?'
        if (!confirm(msg)) return
      }

      this.loading = true
      try {
        if (this.cambiarEstadoPlantas) {
          await axios.put('/api/plantas_estado', {
            plantas: this.plantasSeleccionadas.map(p => ({ id: p.id })),
            nuevoEstado: 'Cosechada'
          })
        }

        const payload = {
          ...this.form,
          lote_id: this.lote_id,
          proyecto_id: this.proyecto_id,
          club_id: this.club_id,
          proyecto_evento_id: this.proyecto_evento_id
        }
        const res = this.isEdit
          ? await axios.put(`/api/biomasa/${this.biomasa.id}`, payload)
          : await axios.post(`/api/biomasa`, payload)

        if (!this.isEdit) {
          await axios.post(`/api/biomasa/${res.data.id}/plantas`, {
            plantaIds: this.plantasSeleccionadas.map(p => p.id)
          })
        }


        alert('Biomasa guardada con éxito.')
        window.scrollTo({ top: 0, behavior: 'smooth' });

        this.$emit('biomasaGuardada', res.data)
      } catch (err) {
        console.error(err)
        alert('Ocurrió un error. Intente nuevamente.')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.biomasa-form {
  max-width: 700px;
  margin: auto;
}

.btn-success {
  background-color: var(--bs-primary);
  border-color: var(--bs-primary);
}

.btn-success:hover {
  background-color: var(--bs-primary-over);
  border-color: var(--bs-primary-over);
}

.card-header.bg-biomasa {
  background-color: #28a745;
  color: white;
}

.card.border-success {
  border-color: #28a745 !important;
}
</style>
